<template>
    <InsightCardLayout role="region" aria-label="Suggested My Work insight" data-testid="my-work-in-chat-insight-prompt">
        <BaseAccordion
            :accordion-ids="[ACCORDION_ID]"
            class="min-w-0"
            trigger-class="min-h-[72px] !p-0 text-left"
            content-class="motion-reduce:animate-none"
            @update:model-value="handleAccordionChange">
            <template #trigger-insight-prompt>
                <div class="min-w-0 flex-1 py-2">
                    <p class="details mb-1 text-valence-grey-600">A fresh insight from your calendar</p>
                    <!-- eslint-disable-next-line vuejs-accessibility/heading-has-content -->
                    <h3 :id="INSIGHT_TITLE_ID" data-testid="my-work-in-chat-insight-title" class="nadia-subtitle text-valence-grey-850">
                        <MarkdownText :text="insight.title" inline />
                    </h3>
                </div>
            </template>
            <template #expandIcon>
                <i class="bi bi-chevron-down leading-none text-valence-grey-600" aria-hidden="true"></i>
            </template>
            <template #collapseIcon>
                <i class="bi bi-chevron-up leading-none text-valence-grey-600" aria-hidden="true"></i>
            </template>
            <template #content-insight-prompt>
                <div class="flex flex-col gap-5 pb-1 pt-4">
                    <MarkdownText
                        :text="insight.summary"
                        class="body prose prose-slate max-w-none text-valence-grey-850 prose-headings:my-1 prose-p:my-0 prose-ul:my-1 prose-ol:my-1 prose-li:my-0" />
                    <InsightEvidenceChips
                        v-if="insight.evidence_groups.length"
                        :evidence-groups="insight.evidence_groups"
                        :analytics-context="{
                            insightId: insight.id,
                            experimentSlug: insight.experiment_slug,
                            location: 'new_chat_prompt',
                            coachingSessionUuid,
                        }" />
                    <div class="flex flex-col gap-3 sm:flex-row-reverse sm:items-center sm:justify-between">
                        <BaseButton
                            theme="primary"
                            size="sm"
                            class="inline-flex min-h-11 w-full items-center justify-center sm:min-h-0 sm:w-auto"
                            :aria-describedby="INSIGHT_TITLE_ID"
                            :disabled="isStartingChat"
                            @click="handleStartInsightConversation">
                            <span class="flex items-center gap-2">
                                <LoadingSpinner v-if="isStartingChat" class="size-4" />
                                <span aria-live="polite">{{ isStartingChat ? "Starting chat..." : "Let’s talk about this" }}</span>
                            </span>
                        </BaseButton>
                        <BaseLink
                            href="/intelligence/my-work/"
                            class="base-btn-tertiary base-btn-sm inline-flex min-h-11 w-full items-center justify-center gap-2 sm:min-h-0 sm:w-auto"
                            @click="handleMyWorkLinkClick">
                            <i class="bi bi-journal-bookmark text-base/none" aria-hidden="true"></i>
                            <span>See all insights in My Work</span>
                        </BaseLink>
                    </div>
                    <p v-if="chatError" role="alert" class="details text-red-600">{{ chatError }}</p>
                </div>
            </template>
        </BaseAccordion>
    </InsightCardLayout>
</template>

<script setup lang="ts">
import type { MyWorkInChatInsight } from "~vue/ChatDetail/types";
import BaseAccordion from "~vue/components/BaseAccordion.vue";
import BaseButton from "~vue/components/BaseButton.vue";
import InsightCardLayout from "~vue/components/InsightCardLayout.vue";
import { useLabInsightChat } from "~vue/composables/useLabInsightChat";
import LoadingSpinner from "~vue/icons/LoadingSpinner.vue";
import { type EvidenceSourceCounts, evidenceSourceCounts } from "~vue/Labs/evidence";
import InsightEvidenceChips from "~vue/Labs/InsightEvidenceChips.vue";
import MarkdownText from "~vue/MarkdownText.vue";
import BaseLink from "~vue/router/BaseLink.vue";
import { openUrl } from "~vue/utils";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { logUserInteraction } from "~vue/utils/logUtils";
import { onMounted } from "vue";

type AccordionState = "expanded" | "collapsed";

type PromptAnalyticsProperties = {
    insight_id: string;
    insight_cycle_id: string;
    experiment_slug: string;
    pipeline_order: number;
    location: "new_chat_prompt";
    coaching_session_uuid?: string;
    accordion_state?: AccordionState;
    evidence_source_counts?: EvidenceSourceCounts;
};

const ACCORDION_ID = "insight-prompt";
const INSIGHT_TITLE_ID = "insight-prompt-title";
const LOCATION = "new_chat_prompt" as const;

const props = withDefaults(
    defineProps<{
        insight: MyWorkInChatInsight;
        coachingSessionUuid?: string | null;
    }>(),
    {
        coachingSessionUuid: null,
    },
);

const { isStartingChat, chatError, handleStartChat } = useLabInsightChat(props.insight.id);

function analyticsProperties(extra: Pick<PromptAnalyticsProperties, "accordion_state" | "evidence_source_counts"> = {}): PromptAnalyticsProperties {
    return {
        insight_id: props.insight.id,
        insight_cycle_id: props.insight.insight_cycle_id,
        experiment_slug: props.insight.experiment_slug,
        pipeline_order: props.insight.pipeline_order,
        location: LOCATION,
        ...(props.coachingSessionUuid ? { coaching_session_uuid: props.coachingSessionUuid } : {}),
        ...extra,
    };
}

function logPromptEvent(eventName: AmplitudeEventNames, extra: Pick<PromptAnalyticsProperties, "accordion_state" | "evidence_source_counts"> = {}) {
    return logUserInteraction(eventName, analyticsProperties(extra), props.coachingSessionUuid ?? undefined);
}

onMounted(() => {
    void logPromptEvent(AmplitudeEventNames.MYWORK_INSIGHT_CARD_SHOWN, {
        evidence_source_counts: evidenceSourceCounts(props.insight.evidence_groups ?? []),
    });
});

function handleAccordionChange(openIds: string[]) {
    void logPromptEvent(AmplitudeEventNames.MYWORK_INSIGHT_PROMPT_ACCORDION_CLICKED, {
        accordion_state: openIds.includes(ACCORDION_ID) ? "expanded" : "collapsed",
    });
}

function handleMyWorkLinkClick() {
    void logPromptEvent(AmplitudeEventNames.MYWORK_INSIGHT_PROMPT_LINK_CLICKED);
}

async function handleStartInsightConversation() {
    void logPromptEvent(AmplitudeEventNames.MYWORK_INSIGHT_CARD_CLICKED);
    const response = await handleStartChat({ navigateOnSuccess: false });
    if (!response) {
        void logPromptEvent(AmplitudeEventNames.MYWORK_INSIGHT_CONVERSATION_FAILED);
        return;
    }

    await logPromptEvent(AmplitudeEventNames.MYWORK_INSIGHT_CONVERSATION_STARTED);
    openUrl(response.coaching_session_url);
}
</script>
